import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDateToShortString } from '../../../common/common';

const UniqueCustomerModel = ({ isOpen, onClose, order_details_id, sku, isSingleExclude, isBoth, productList }) => {
    const [loadingdata, setLoadingdata] = useState(false);
    const navigate = useNavigate();
    const [originalCustomerResponse, setOriginalCustomerResponse] = useState([]);
    const [customerResponse, setCustomerResponse] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const getCustomerData = async () => {
        try {
            setLoadingdata(true);


            const params = new URLSearchParams({ order_details_id: order_details_id, isSingleExclude: isSingleExclude });
            let finalSku = [];
            if (isBoth.antecedent) {
                finalSku = [...sku.antecedentSKU]
            } else if (isBoth.consequent) {
                finalSku = [...sku.consequentSKU]
            } else {
                finalSku = [...sku.allSku]
            }
            finalSku.forEach((sku) => params.append("sku", sku)); // Appends multiple sku=...

            const url = `${process.env.REACT_APP_API_END_POINT}/data-analysis/customers-details?${params.toString()}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            });

            const data = await response.json();
            if (!response.ok) {
                if (data.detail.isExpireToken) {
                    const token = localStorage.removeItem("token");
                    const user = localStorage.removeItem("userInfo");
                    navigate("/login")
                }
                throw new Error(data.detail?.message || 'Failed to fetch orders');
            }

            // 🔍 Filter unique by customerId
            // const uniqueByCustomerId = Array.from(
            //     new Map(data.data.map(item => [item.customerId, item])).values()
            // );

            const groupedRaw = data.data.reduce((acc, item) => {
                const id = item.order_id;
                if (!acc[id]) acc[id] = [];
                acc[id].push(item);
                return acc;
            }, {});

            
            const filteredGrouped = Object.entries(groupedRaw).reduce((acc, [orderId, items]) => {
                const skusInOrder = items.map(i => i.sku.toLowerCase());
                const hasAllRequiredSkus = finalSku.every(sku => skusInOrder.includes(sku.toLowerCase()));

                if (hasAllRequiredSkus) {
                    // Extract customer & order info from first item
                    const { customerId, customerFirstName, customerLastName, customerPhone, order_id, customerEmail, order_created_at } = items[0];

                    // Map all products
                    const products = items.map(({ sku, product_name, product_image }) => ({
                        sku,
                        product_name,
                        product_image
                    }));

                    acc[orderId] = {
                        customerId,
                        customerFirstName,
                        customerLastName,
                        customerEmail,
                        customerPhone,
                        order_id,
                        products,
                        order_created_at
                    };
                }

                return acc;
            }, {});

            const arrayData = Object.values(filteredGrouped);
            // 🔍 Filter unique by customerId
            const uniqueByCustomerId = Array.from(
                new Map(arrayData.map(item => [item.customerId, item])).values()
            );
            setCustomerResponse(uniqueByCustomerId)
            setOriginalCustomerResponse(uniqueByCustomerId)

        } catch (err) {
            toast.error(err.message, {
                position: "top-right",
                autoClose: 3000,
            });
        } finally {
            setLoadingdata(false)
        }
    };


    useEffect(() => {
        if (isOpen) {
            setCurrentPage(1);
            getCustomerData();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!searchTerm.trim()) {
          setCustomerResponse(originalCustomerResponse); // Reset
          return;
        }
      
        const lower = searchTerm.toLowerCase();
      
        const filtered = originalCustomerResponse.filter((customer) => {
            const fullName = `${customer.customerFirstName ?? ''} ${customer.customerLastName ?? ''}`.toLowerCase();
            return (
              fullName.includes(lower) ||
              customer.customerPhone?.toLowerCase().includes(lower) ||
              customer.customerEmail?.toLowerCase().includes(lower) ||
              customer.customerId?.toLowerCase().includes(lower)
            );
          });
      
        setCustomerResponse(filtered);
      }, [searchTerm]);

    // Pagination logic
    const totalPages = Math.ceil(customerResponse.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedGroups = customerResponse.slice(startIndex, startIndex + itemsPerPage);
    

    if (!isOpen) return null;

    return (
        <div className="fixed w-full inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">

            <div className="bg-white w-full max-w-7xl max-h-[90vh] rounded-lg shadow-lg flex flex-col">
                {loadingdata &&
                    <div className="flex items-center justify-center min-h-[200px]">
                        <div className="flex flex-col items-center space-y-2">
                            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-gray-600 text-sm">Loading...</p>
                        </div>
                    </div>
                }

                {
                    !loadingdata &&
                    <>
                        {/* Header */}
                        < div className="p-4 border-b flex justify-end items-center">
                            {/* <h2 className="text-lg font-semibold text-gray-800">Customers Information</h2> */}
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">&times;</button>
                        </div>


                        {/* Scrollable table section */}
                        <div className="overflow-y-auto flex-1">
                            <div className='p-4 '>
                                {
                                    (isBoth.antecedent || isBoth.all) &&
                                    <>
                                        <p className="text-lg font-semibold text-gray-700 mb-2">Antecedents </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                                            {productList.antecedents.map((data, index) => (
                                                <div
                                                    key={`antecedent-${index}`}
                                                    className="border rounded-lg p-4 flex space-x-3 items-start bg-white shadow-sm hover:shadow-md"
                                                >
                                                    <img
                                                        src={data.image || "noimage.jpg"}
                                                        alt={data.product_name}
                                                        className="w-12 h-12 rounded object-cover border"
                                                    />
                                                    <div>
                                                        <p className="font-medium">{data.product_title}</p>
                                                        <p className="text-xs text-gray-500">
                                                            <b>SKU:</b> {data.sku}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                }

                                {
                                    (isBoth.consequent || isBoth.all) &&
                                    <>
                                        <p className="text-lg font-semibold text-gray-700 mb-2">Consequent </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                            {productList.consequent.map((data, index) => (
                                                <div
                                                    key={`consequent-${index}`}
                                                    className="border rounded-lg p-4 flex space-x-3 items-start bg-white shadow-sm hover:shadow-md"
                                                >
                                                    <img
                                                        src={data.image || "noimage.jpg"}
                                                        alt={data.product_name}
                                                        className="w-12 h-12 rounded object-cover border"
                                                    />
                                                    <div>
                                                        <p className="font-medium">{data.product_title}</p>
                                                        <p className="text-xs text-gray-500">
                                                            <b>SKU:</b> {data.sku}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                }


                            </div>
                            <div className="p-4 border-b bg-white shadow-sm flex flex-col md:flex-row md:justify-between md:items-center space-y-3 md:space-y-0">
                                {/* Left Side: Info Text */}
                                <p className="text-gray-700 text-sm md:text-base">
                                    <span className="font-semibold text-indigo-600">{originalCustomerResponse.length} unique customers</span> purchased items
                                </p>

                                {/* Right Side: Search Input */}
                                <div className="w-full md:w-auto">
                                    <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="🔍 Search by Customer ID, Name or Phone"
                                    className="w-full md:w-80 px-4 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition"
                                    />
                                </div>
                            </div>
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Customer's ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Customer's Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Customer's Phone</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Customer's Email</th>

                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedGroups.length > 0 ? (
                                        paginatedGroups.map((custData, index) => (
                                            <React.Fragment key={custData.customerId}>
                                                <tr >
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                        # {custData.customerId}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                        {custData.customerFirstName} {custData.customerLastName}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                        {custData.customerPhone ? custData.customerPhone : "N/A"}

                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                        {custData.customerEmail ? custData.customerEmail : "N/A"}

                                                    </td>
                                                </tr>
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                                                No Line Items Found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {
                            totalPages > 1 &&
                            <div className="p-4 border-t flex justify-between items-center bg-gray-50 flex-wrap gap-2">
                                <button
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                                >
                                    Previous
                                </button>

                                <div className="flex items-center space-x-1">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                        .filter((page) => {
                                            if (
                                                page === 1 ||
                                                page === totalPages ||
                                                Math.abs(page - currentPage) <= 1
                                            ) {
                                                return true;
                                            }
                                            return false;
                                        })
                                        .reduce((acc, page, idx, arr) => {
                                            if (idx > 0 && page - arr[idx - 1] > 1) {
                                                acc.push("ellipsis");
                                            }
                                            acc.push(page);
                                            return acc;
                                        }, [])
                                        .map((page, idx) =>
                                            page === "ellipsis" ? (
                                                <span key={idx} className="px-2 text-gray-500">...</span>
                                            ) : (
                                                <button
                                                    key={page}
                                                    onClick={() => setCurrentPage(page)}
                                                    className={`px-3 py-1 text-sm rounded ${currentPage === page
                                                        ? "bg-blue-500 text-white"
                                                        : "bg-gray-200 text-gray-700"
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            )
                                        )}
                                </div>

                                <button
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded disabled:opacity-50"
                                >
                                    Next
                                </button>
                            </div>
                        }
                    </>
                }


            </div>
        </div >
    );
};

export default UniqueCustomerModel;
